import type { FieldOption, FormField } from '@/types/form-definition';

const DEFAULT_CHOICES: FieldOption[] = [
  { value: 'Option 1', label: 'Option 1' },
  { value: 'Option 2', label: 'Option 2' },
];

function rawAllowedValues(field: FormField): string[] {
  const raw = field.validation?.allowedValues;
  if (Array.isArray(raw)) {
    const strings = raw.filter((x): x is string => typeof x === 'string' && x.trim().length > 0);
    if (strings.length > 0) return strings;
  }
  return DEFAULT_CHOICES.map((o) => o.value);
}

function parseOptionsFromConfig(field: FormField): FieldOption[] | null {
  const raw = field.config?.options;
  if (!Array.isArray(raw)) return null;
  const out: FieldOption[] = [];
  for (const item of raw) {
    if (item && typeof item === 'object') {
      const rec = item as Record<string, unknown>;
      const value = typeof rec.value === 'string' ? rec.value.trim() : '';
      if (!value) continue;
      const label =
        typeof rec.label === 'string' && rec.label.trim().length > 0 ? rec.label.trim() : value;
      const opt: FieldOption = { value, label };
      if (rec.disabled === true) opt.disabled = true;
      out.push(opt);
    }
  }
  return out.length > 0 ? out : null;
}

/** Resolved choice rows: prefers `config.options` when non-empty; otherwise derives labels from `validation.allowedValues`. */
export function getChoiceOptions(field: FormField): FieldOption[] {
  const fromConfig = parseOptionsFromConfig(field);
  if (fromConfig && fromConfig.length > 0) return fromConfig;
  return rawAllowedValues(field).map((v) => ({ value: v, label: v }));
}

export function getAllowedValues(field: FormField): string[] {
  return getChoiceOptions(field).map((o) => o.value);
}

export function patchChoiceOptions(field: FormField, options: FieldOption[]): Partial<FormField> {
  const cleaned: FieldOption[] = options
    .map((o) => {
      const value = o.value.trim();
      if (!value) return null;
      const label = o.label?.trim() || value;
      const next: FieldOption = { value, label };
      if (o.disabled) next.disabled = true;
      return next;
    })
    .filter((x): x is FieldOption => x !== null);

  const finalOpts =
    cleaned.length > 0 ? cleaned : [{ value: 'Option 1', label: 'Option 1' } satisfies FieldOption];
  const values = finalOpts.map((o) => o.value);

  return {
    config: {
      ...field.config,
      options: finalOpts,
    },
    validation: {
      ...field.validation,
      allowedValues: values,
    },
  };
}

/** Legacy helper: value-only list; sets both `config.options` and `validation.allowedValues`. */
export function patchAllowedValues(field: FormField, values: string[]): Partial<FormField> {
  const cleaned = values.map((s) => s.trim()).filter(Boolean);
  const strings = cleaned.length > 0 ? cleaned : ['Option 1'];
  return patchChoiceOptions(
    field,
    strings.map((v) => ({ value: v, label: v }))
  );
}

export function getMinSelections(field: FormField): number | undefined {
  const v = field.validation?.minSelections;
  return typeof v === 'number' && v >= 0 ? v : undefined;
}

export function getMaxSelections(field: FormField): number | undefined {
  const v = field.validation?.maxSelections;
  return typeof v === 'number' && v >= 0 ? v : undefined;
}
